import { BUILT_IN_SKILL_ID_PREFIX } from '@server/types';
import { ScrollText } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/_shared/components/ui/badge';
import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';
import { getSkillPath } from '@/_shared/lib/skills';
import type { SSEMessage } from '@/_shared/types';

type SkillInvokedMessage = Extract<SSEMessage, { type: 'skill_invoked' }>;

interface SkillInvokedChipProps {
  message: SkillInvokedMessage;
}

/**
 * Renders a snapshot for a slash-invoked skill. Stored workspace skills link to their detail page;
 * code-defined built-ins are display-only because they have no Context Hub row or detail route.
 *
 * Displays the skill title from the SSE row snapshot. Never re-fetches from
 * the context entry, so the chip still renders after a skill is renamed or
 * deleted (Named Invariant #3: display-only). A deleted workspace skill may land on its not-found
 * state; a built-in never emits that dead link.
 */
export function SkillInvokedChip({ message }: SkillInvokedChipProps): React.ReactElement {
  const workspace = useWorkspaceOrNull();
  const path = getSkillPath(message.skillId);
  const title =
    workspace && !message.skillId.startsWith(BUILT_IN_SKILL_ID_PREFIX) ? (
      <Link
        className="font-medium text-primary hover:underline"
        to={workspace.wp(path)}
        aria-label={`Skill: ${message.title}`}
      >
        {message.title}
      </Link>
    ) : (
      <span className="font-medium text-foreground">{message.title}</span>
    );

  return (
    <div
      className="mx-6 my-1 rounded-lg border border-border bg-background p-2.5 text-xs"
      data-testid="skill-invoked-chip"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Badge variant="highlight" bordered className="gap-1 text-xs">
          <ScrollText className="size-3" aria-hidden="true" />
          Skill
        </Badge>
        <span className="min-w-0 truncate">{title}</span>
      </div>
    </div>
  );
}
